import { carryExif } from 'solomon-next-reporting-ui/utils/exif';

// The largest render in the app is ImageView's modal at @width={{1024}}
// (image-view.hbs:24), so 1600 leaves headroom for a larger view later while
// cutting a 4032x3024 phone photo to 1600x1200.
const MAX_IMAGE_DIMENSION = 1600;

const JPEG_QUALITY = 0.8;

// ImageUpload runs the reductions one after another, so a decode or an encode
// that settles neither way would stall every photo selected after it for the
// life of the component, not just its own. Ten seconds is far longer than a
// 12MP photo takes to decode and re-encode on a slow phone, so the only cost of
// hitting it is one photo going up at full size.
const REDUCTION_TIMEOUT = 10000;

/**
 * Reduces an image to at most MAX_IMAGE_DIMENSION on its longest edge, or
 * returns null when the original should be uploaded untouched.
 *
 * Never throws: losing the photo would be far worse than uploading a large one.
 *
 * A canvas re-encode carries the pixels and nothing else, so a JPEG has its
 * original EXIF put back by carryExif with the orientation tag reset to 1. The
 * upload API reads GPS, the date taken and the camera make and model off the
 * file and stores them (image-helper getImageData), and losing those on every
 * photo taken in the field would be a poor trade for the bandwidth.
 *
 * A PNG cannot carry EXIF and keeps only its pixels. In practice that means a
 * screenshot rather than a photo off a camera, which has little to lose.
 *
 * Two things do not survive either way. The ICC profile goes, so a Display P3
 * photo from an iPhone is flattened to untagged sRGB. And the EXIF pixel
 * dimension tags still describe the original, because they are copied across
 * untouched - nothing reads them today, and rewriting them would mean editing
 * offsets rather than a value in place.
 */
export async function reduceImage(file: File): Promise<Blob | null> {
  let timer = 0;

  // Racing only stops this function waiting; the decode or encode it gave up on
  // carries on holding the photo. Aborting gives them something to listen for,
  // so the memory goes back rather than being pinned until the tab is closed.
  const abandoned = new AbortController();

  const abandon = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      abandoned.abort();
      resolve(null);
    }, REDUCTION_TIMEOUT);
  });

  try {
    return await Promise.race([reduce(file, abandoned.signal), abandon]);
  } finally {
    clearTimeout(timer);
  }
}

async function reduce(file: File, signal: AbortSignal): Promise<Blob | null> {
  try {
    // A canvas can only re-encode JPEG and PNG. toBlob quietly substitutes PNG
    // for anything else, so a GIF would become PNG bytes under a .gif name, and
    // an animated one would lose every frame but the first.
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      return null;
    }

    const image = await decodeImage(file, signal);

    if (!image || signal.aborted) {
      return null;
    }

    // naturalWidth/naturalHeight already account for the EXIF orientation tag
    // and drawImage rotates to match, so a portrait phone photo is drawn the
    // right way up. Canvas output carries no EXIF, so it is rotated once only.
    const scale = MAX_IMAGE_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight);

    // A second pass through the encoder would only add artefacts.
    if (scale >= 1) {
      return null;
    }

    const canvas = document.createElement('canvas');

    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);

    const context = canvas.getContext('2d');

    if (!context) {
      return null;
    }

    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    // toBlob cannot be called off, and it holds the canvas until it answers.
    // Zeroing the dimensions drops the backing store, which is the only way to
    // get a full size canvas back from an encode that never returns. Whatever
    // it eventually produces is discarded, so spoiling it costs nothing.
    signal.addEventListener(
      'abort',
      () => {
        canvas.width = 0;
        canvas.height = 0;
      },
      { once: true },
    );

    const encoded = await encodeImage(canvas, file.type);

    // toBlob reports failure with null, and substitutes PNG for a type it
    // cannot encode.
    if (!encoded || encoded.type !== file.type) {
      return null;
    }

    // Only JPEG can carry it. A PNG keeps its pixels and loses its metadata,
    // which in practice means a screenshot rather than a photo off a camera.
    const reduced = file.type === 'image/jpeg' ? await carryExif(file, encoded) : encoded;

    // Measured after the metadata goes back on, so the comparison is against
    // what actually gets sent. A downscale can encode larger than it started,
    // which would mean sending more over mobile data, not less.
    if (reduced.size >= file.size) {
      return null;
    }

    return reduced;
  } catch (error) {
    console.error(error);

    return null;
  }
}

function decodeImage(file: File, signal: AbortSignal): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    const finish = (result: HTMLImageElement | null) => {
      signal.removeEventListener('abort', giveUp);
      // Only revoked once the decode has settled one way or the other; doing it
      // earlier would pull the bytes out from under a decode still reading them.
      URL.revokeObjectURL(url);
      resolve(result);
    };

    // Clearing src stops a decode that has not settled, which releases both the
    // partly decoded bitmap and the file behind the object URL. Without it a
    // photo that never loads is held for the life of the page.
    const giveUp = () => {
      image.src = '';
      finish(null);
    };

    if (signal.aborted) {
      URL.revokeObjectURL(url);
      resolve(null);

      return;
    }

    signal.addEventListener('abort', giveUp, { once: true });

    image.onload = () => finish(image);
    image.onerror = () => finish(null);
    image.src = url;
  });
}

function encodeImage(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, JPEG_QUALITY));
}
